<template>
	<div class="search-journey">
		<button
			class="search-journey__button button button--normal"
			:class="{
				'button--primary': !isCalendar,
				'button--secondary': isCalendar,
			}"
			@click="postJourneySearch"
			:disabled="isButtonDisabled"
		>
			<svg v-if="prevIcon" class="search-journey__button-icon prev">
				<use :xlink:href="`#${chevron}`" />
			</svg>
			<span class="search-journey__button-label">{{ buttonText }}</span>
			<svg v-if="nextIcon" class="search-journey__button-icon next">
				<use :xlink:href="`#${chevron}`" />
			</svg>
		</button>
		<p v-if="errorMessage" class="error-message">
			{{ errorMessage }}
		</p>
	</div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useJourneyStore } from "@/stores/journeyStore";
import { useBookingStore } from "@/stores/bookingStore";
import { useErrorStore } from "@/stores/errorStore";
import { useMyTravelStore } from "@/stores/myTravelStore";
import { useI18n } from "vue-i18n";
import { postJourneySearch as postJourneySearchService } from "@/services/journeyService";
import { parseISO, subDays, addDays, format } from "date-fns";
import { useDebounce } from "@/composables/useDebounce";
import chevron from "@/assets/icons/chevron.svg";

export default defineComponent({
	name: "SearchJourney",
	props: {
		disabled: {
			type: Boolean,
			default: false,
		},
		isCalendar: {
			type: Boolean,
			default: false,
		},
		buttonText: {
			type: String,
			default: "Fortsett",
		},
		showOutbound: {
			type: Boolean,
			default: true,
		},
		prevIcon: {
			type: Boolean,
			default: false,
		},
		nextIcon: {
			type: Boolean,
			default: false,
		},
		nextRoute: {
			type: Object,
			required: true,
		},
	},
	emits: ["close"],
	setup(props, { emit }) {
		const journeyStore = useJourneyStore();
		const bookingStore = useBookingStore();
		const errorStore = useErrorStore();
		const myTravelStore = useMyTravelStore();
		const router = useRouter();
		const route = useRoute();
		const { t } = useI18n();
		const errorMessage = ref("");
		const { debounce } = useDebounce();

		const isRebooking = computed(() => {
			return route.path.toLowerCase().includes("rebook") || route.query.isRebooking === "true";
		});

		const passengers = computed(() => {
			// If we're rebooking, use the passengers from the existing booking
			if (isRebooking.value && bookingStore.bookingData?.passengers) {
				return bookingStore.bookingData.passengers.map((passenger) => ({
					id: passenger.id,
					type: passenger.type,
					discount_cards: passenger.discount_cards || [],
				}));
			}
			// Otherwise use the passengers from the journey store (normal booking flow)
			return journeyStore.passengers;
		});

		const isButtonDisabled = computed(() => {
			return (
				props.disabled ||
				!passengers.value ||
				passengers.value.length === 0 ||
				!journeyStore.OriginStation._u_i_c_station_code ||
				!journeyStore.destinationStation ||
				!journeyStore.departureDate
			);
		});

		const performJourneySearch = async () => {
			const travels = [
				{
					origin: journeyStore.OriginStation._u_i_c_station_code,
					destination: journeyStore.destinationStation._u_i_c_station_code,
					direction: "outbound",
					departure: journeyStore.departureDate,
					product_types: ["ST", "TO"],
				},
			];

			// Always include return journey if it exists
			if (journeyStore.returnDate) {
				travels.push({
					origin: journeyStore.destinationStation._u_i_c_station_code,
					destination: journeyStore.OriginStation._u_i_c_station_code,
					direction: "inbound",
					departure: journeyStore.returnDate,
					product_types: ["ST", "TO"],
				});
			}

			try {
				if (!props.isCalendar) {
					myTravelStore.setDates("", null);
					myTravelStore.setOutboundTravel(null);
					myTravelStore.setInboundTravel(null);
					myTravelStore.updatePassengerCounts("outbound", [], []);
					myTravelStore.updatePassengerCounts("inbound", [], []);
					myTravelStore.setHasReturnTravel(!!journeyStore.returnDate);
				}

				const response = await postJourneySearchService({ travels, passengers: passengers.value });
				if (response?.data?.offer?.travels?.length > 0) {
					journeyStore.setJourneySearchResults(response);
					if (isRebooking.value) {
						journeyStore.setPassengers(passengers.value);
					}
					emit("close");
				} else {
					errorStore.setError("No travels found");
				}
			} catch (error: any) {
				errorStore.setError(error.message);
			}
		};

		const debouncedJourneySearch = debounce(performJourneySearch, 500);

		const postJourneySearch = async () => {
			// For calendar navigation, just update the date and search
			if (props.isCalendar) {
				const currentDate = parseISO(props.showOutbound ? journeyStore.departureDate : journeyStore.returnDate);
				const newDate = props.prevIcon ? subDays(currentDate, 1) : addDays(currentDate, 1);

				// Update the appropriate date based on which direction we're viewing
				if (props.showOutbound) {
					journeyStore.setDepartureDate(format(newDate, "yyyy-MM-dd"));
				} else {
					journeyStore.setReturnDate(format(newDate, "yyyy-MM-dd"));
				}

				// Use debounced search for calendar navigation
				debouncedJourneySearch();
				return;
			}

			// Original functionality for non-calendar usage
			if (!passengers.value) {
				errorMessage.value = "Passenger type is not set";
				return;
			} else if (!journeyStore.OriginStation._u_i_c_station_code) {
				errorMessage.value = "Origin station is not set";
				return;
			} else if (!journeyStore.destinationStation) {
				errorMessage.value = "Destination station is not set";
				return;
			} else if (!journeyStore.departureDate) {
				errorMessage.value = "Departure date is not set";
				return;
			}

			// For non-calendar searches, perform the search immediately
			await performJourneySearch();

			// Navigate with the correct query parameters for rebooking
			if (isRebooking.value) {
				router.push({
					name: "SearchResultPage",
					query: {
						isRebooking: "true",
						bookingReference: route.query.bookingReference as string,
					},
				});
			} else {
				router.push(props.nextRoute); // Should include query parameters
			}
			emit("close");
		};

		onMounted(() => {
			if (bookingStore.outboundTravel || bookingStore.inboundTravel) {
				bookingStore.$reset();
			}
		});

		return {
			postJourneySearch,
			isButtonDisabled,
			errorMessage,
			chevron,
		};
	},
});
</script>

<style lang="scss">
.search-journey {
	&__button {
		display: inline-flex;
		flex-direction: row;
		align-items: center;
		gap: 8px;

		.calendar-search__buttons & {
			background-color: transparent;
			border: none;
			&:disabled {
				background: transparent;
				border: none;
				color: $clr-text-disable;

				&:hover {
					background: transparent;
				}
			}
		}

		&-icon {
			position: relative;
			top: 1px;
			width: 16px;
			height: 16px;

			&.prev {
				transform: rotate(-90deg);
			}

			&.next {
				transform: rotate(90deg);
			}
		}
	}

	.error-message {
		color: red;
		margin-top: 1rem;
	}
}
</style>
